import React, { useState, useRef, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn } from "@/components/common";
import { PaperPlaneRight, Sparkle, User, CircleNotch, Info, X } from "@phosphor-icons/react";

export function AskAIDrawer({ open, onClose, pendingPrompt, clearPendingPrompt }) {
  const { selectedStudentId } = useAuth();
  const [question, setQuestion] = useState("");
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread]);

  useEffect(() => {
    if (!open) return;
    if (pendingPrompt) {
      setQuestion(pendingPrompt);
      clearPendingPrompt?.();
    }
    setTimeout(() => {
      const el = inputRef.current;
      if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
    }, 50);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  const send = async () => {
    const trimmed = question.trim();
    if (!trimmed || loading) return;
    setThread((t) => [...t, { role: "user", text: trimmed }]);
    setQuestion("");
    setLoading(true);
    try {
      const res = await api.post("/helper/ask", {
        student_id: selectedStudentId || undefined,
        question: trimmed,
      });
      setThread((t) => [...t, { role: "assistant", text: res.data.answer }]);
      if (res.data.remaining_today != null) setRemaining(res.data.remaining_today);
    } catch (e) {
      setThread((t) => [...t, { role: "assistant", text: apiErr(e) || "Something went wrong." }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
      <div
        className={`fixed right-0 top-0 z-[61] flex h-full w-full flex-col border-l border-[#C2C9D1] bg-white shadow-2xl transition-transform duration-200 sm:w-[400px] ${
          open ? "translate-x-0" : "translate-x-full pointer-events-none"
        }`}
        data-testid="ai-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#C2C9D1] px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkle size={20} weight="bold" className="text-[#034AA2]" />
            <span className="text-sm font-extrabold">Ask AI</span>
          </div>
          <button onClick={onClose} data-testid="ai-drawer-close" aria-label="Close" className="rounded-md p-1.5 text-[#6b7280] hover:bg-[#E9E9EB]">
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Thread */}
        <div className="flex-1 overflow-y-auto" data-testid="ai-thread">
          {thread.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center" data-testid="ai-empty-state">
              <Sparkle size={40} weight="duotone" className="text-[#034AA2]/30" />
              <div className="max-w-[280px] text-xs text-[#6b7280]">
                Ask anything about using the app, your expenses, withdrawals, or 529 rules.
              </div>
            </div>
          ) : (
            <div className="divide-y divide-[#E9E9EB]">
              {thread.map((msg, i) => (
                <div key={i} className={`flex gap-2.5 px-4 py-3 ${msg.role === "assistant" ? "bg-[#F7F8FA]" : ""}`} data-testid={`ai-msg-${i}`}>
                  <div className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${msg.role === "assistant" ? "bg-[#034AA2] text-white" : "bg-[#E9E9EB] text-[#475569]"}`}>
                    {msg.role === "assistant" ? <Sparkle size={13} weight="bold" /> : <User size={13} weight="bold" />}
                  </div>
                  <div className="min-w-0 flex-1 text-[13px] leading-relaxed text-[#1f2937] whitespace-pre-wrap" data-testid={`ai-msg-text-${i}`}>
                    {msg.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-2.5 bg-[#F7F8FA] px-4 py-3" data-testid="ai-loading">
                  <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#034AA2] text-white">
                    <CircleNotch size={13} weight="bold" className="animate-spin" />
                  </div>
                  <div className="text-[13px] text-[#6b7280]">Thinking…</div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#C2C9D1] p-3 space-y-2">
          <div className="flex items-center gap-1.5 text-[10px] text-[#6b7280]" data-testid="ai-disclaimer">
            <Info size={11} weight="fill" className="shrink-0 text-[#034AA2]" />
            <span>Answers are grounded in your records and general 529 rules. Not tax advice.</span>
            {remaining != null && (
              <span className="ml-auto shrink-0 font-semibold" data-testid="ai-remaining">
                {remaining} left
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              ref={inputRef}
              data-testid="ai-input"
              className="h-9 flex-1 rounded-md border border-[#C2C9D1] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#034AA2]"
              placeholder="Ask a question…"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={onKey}
              maxLength={500}
              disabled={loading}
            />
            <Btn
              data-testid="ai-send-btn"
              onClick={send}
              disabled={!question.trim() || loading}
              className="h-9 px-3"
            >
              <PaperPlaneRight size={16} weight="bold" />
            </Btn>
          </div>
        </div>
      </div>
  );
}
